import { heardFromLabels, heardFromValues, type AdminSignupSource, type HeardFrom } from "@teamna/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Badge, Callout, ToggleRow, showToast } from "@teamna/ui/components/index.ts";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { useConfirmWarmIntroTag, useSetWarmIntroTag } from "../lib/cc-api.ts";

/**
 * The source of each sign-up in Control Center (website traffic plan, task 0.7, and section 10 for
 * the numbers it feeds). The answer to "How did you hear about Teamna?", the referrer name, the
 * website campaign, and the "Warm intro" tag that only Teamna staff set.
 *
 * The rule: a business is a warm intro when the answer is "Anas or Lana" or the tag is on.
 */

/** The answer as words. Null: the business was made before the question, or by a client that does not ask it. */
export function heardFromText(heardFrom: HeardFrom | null): string {
  return heardFrom ? heardFromLabels[heardFrom] : "Not asked";
}

export type SourceFilter = "all" | "warm" | "not_warm" | "not_asked" | HeardFrom;

export const SOURCE_FILTERS: { id: SourceFilter; label: string }[] = [
  { id: "all", label: "All sources" },
  { id: "warm", label: "Warm intro" },
  { id: "not_warm", label: "Not a warm intro" },
  ...heardFromValues.map((id) => ({ id, label: `Heard from: ${heardFromLabels[id]}` })),
  { id: "not_asked", label: "Not asked" },
];

export function matchesSourceFilter(source: AdminSignupSource, filter: SourceFilter): boolean {
  if (filter === "all") return true;
  if (filter === "warm") return source.warmIntro;
  if (filter === "not_warm") return !source.warmIntro;
  if (filter === "not_asked") return source.heardFrom === null;
  return source.heardFrom === filter;
}

/**
 * The list cell: the answer and a badge for a warm intro. Kept narrow, because the list is already
 * wide at 1280px; the campaign values are on the detail.
 */
export function SourceCell({ source }: { source: AdminSignupSource }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className={source.heardFrom ? "text-ink" : "text-ink-3"}>{heardFromText(source.heardFrom)}</span>
      {source.warmIntro ? <Badge tone="confirmed">Warm intro</Badge> : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow text-ink-2">{label}</dt>
      <dd className="mt-0.5 text-[14px] [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function none(value: string | null): ReactNode {
  return value ?? <span className="text-ink-3">None</span>;
}

/** The card on the business detail. The tag is the one thing staff change here. */
export function SignupSourceCard({ businessId, source }: { businessId: string; source: AdminSignupSource }) {
  const setTag = useSetWarmIntroTag(businessId);
  const confirm = useConfirmWarmIntroTag(businessId);
  // True after a failed change whose read-back failed too: the switch can show an old value. The
  // detail mounts this card with `key={businessId}`, so the state never moves to another business.
  const [unsure, setUnsure] = useState(false);
  // The read-back after a failed change. The switch stays disabled until it ends, so no second change
  // can start while the first one is not known.
  const [confirming, setConfirming] = useState(false);
  // Each change has a number. A late answer of an older change is ignored (review F4.2).
  const attempt = useRef(0);
  // No toast about this card after it has closed (review F5.2).
  const open = useRef(true);
  useEffect(() => {
    open.current = true;
    return () => {
      open.current = false;
    };
  }, []);
  const byAnswer = source.heardFrom === "founders";

  const change = (on: boolean) => {
    const mine = ++attempt.current;
    setTag.mutate(on, {
      onSuccess: (next) => {
        if (mine !== attempt.current) return;
        setUnsure(false);
        showToast(next.warmIntroTag ? "Tagged as a warm intro." : "The warm intro tag is off.");
      },
      onError: (error) => {
        // The change can be saved although the answer failed. Show the saved value, or say we do not know it.
        setConfirming(true);
        void confirm().then((read) => {
          if (mine !== attempt.current || !open.current) return;
          setConfirming(false);
          setUnsure(!read);
          const why = isApiError(error) && error.detail ? `${error.detail} ` : "";
          showToast(read ? `${why}We could not confirm the change. The switch shows the saved value.` : `${why}We could not confirm the change.`);
        });
      },
    });
  };

  return (
    <section aria-labelledby="signup-source-heading" className="mb-8 rounded-card border border-line bg-paper p-5 shadow-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 id="signup-source-heading" className="text-[17px]">
          Sign-up source
        </h2>
        {source.warmIntro ? <Badge tone="confirmed">Warm intro: yes</Badge> : <Badge tone="quiet">Warm intro: no</Badge>}
      </div>
      <dl className="mb-2 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-5">
        <Fact label="How they heard about Teamna">{heardFromText(source.heardFrom)}</Fact>
        <Fact label="Referrer name">{source.referrerName ? <span dir="auto">{source.referrerName}</span> : none(null)}</Fact>
        <Fact label="utm_source">{none(source.utmSource)}</Fact>
        <Fact label="utm_medium">{none(source.utmMedium)}</Fact>
        <Fact label="utm_campaign">{none(source.utmCampaign)}</Fact>
      </dl>
      {unsure ? (
        <Callout tone="late" title="We do not know the saved tag">
          Control Center could not confirm the change and could not read the business again. Reload the page before you use or change the warm intro tag.
        </Callout>
      ) : null}
      <ToggleRow
        label="Warm intro tag"
        hint={
          byAnswer
            ? "The answer is \"Anas or Lana\", so this business is a warm intro with or without the tag. Teamna staff only."
            : "Turn it on when Anas or Lana brought this business. Teamna staff only. The change goes in the audit log."
        }
        checked={source.warmIntroTag}
        // While the saved value is not known, the switch could change the tag the wrong way (review F5.1).
        disabled={setTag.isPending || confirming || unsure}
        onChange={change}
      />
    </section>
  );
}
